import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";

const THEME = {
  accent: "#86EFAC",
  surface: "#F8FDF9",
  accentSoft: "rgba(134, 239, 172, 0.35)",
  triggerOpen: "border-[#86D4A4] ring-2 ring-[rgba(134,239,172,0.35)]",
  triggerIdle: "border-[#DADDE1] hover:border-[#86D4A4]",
};

function ThemeCheckbox({ checked }) {
  return (
    <span
      className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
        checked
          ? "bg-gray-900 border-gray-900"
          : "bg-white border-gray-300"
      }`}
    >
      {checked && <Check size={11} className="text-white" strokeWidth={3} />}
    </span>
  );
}

/**
 * Multi-select checkbox dropdown filter — matches Templates page theme.
 */
export default function TemplateCheckboxFilter({
  label,
  options = [],
  selectedIds = [],
  onApply,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(selectedIds);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPending(selectedIds);
    }
  }, [selectedIds, open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const allIds = options.map((o) => o.id);
  const allSelected =
    allIds.length > 0 && allIds.every((id) => pending.includes(id));

  const buttonLabel = () => {
    if (!selectedIds.length) return label;
    if (selectedIds.length === 1) {
      const opt = options.find((o) => o.id === selectedIds[0]);
      return opt?.label || label;
    }
    return `${selectedIds.length} options selected`;
  };

  const toggleId = (id) => {
    setPending((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    setPending(allSelected ? [] : [...allIds]);
  };

  const handleApply = () => {
    onApply(pending);
    setOpen(false);
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`tpl-fchip ${open ? THEME.triggerOpen : THEME.triggerIdle}${selectedIds.length ? " on" : ""}`}
      >
        <span className="truncate text-left">{buttonLabel()}</span>
        <ChevronDown
          size={16}
          className={`text-gray-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className="tpl-fmenu absolute left-0 z-50 mt-2 min-w-[240px] max-w-[320px] overflow-hidden"
        >
          <div className="max-h-[320px] overflow-y-auto py-1">
            {options.length > 1 && (
              <button
                type="button"
                onClick={toggleAll}
                className="tpl-fopt"
              >
                <ThemeCheckbox checked={allSelected} />
                <span className="font-medium">Select all</span>
              </button>
            )}
            {options.map((opt) => {
              const checked = pending.includes(opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => toggleId(opt.id)}
                  className={`tpl-fopt${checked ? " on" : ""}`}
                >
                  <ThemeCheckbox checked={checked} />
                  <span className="text-gray-800">{opt.label}</span>
                </button>
              );
            })}
            {options.length === 0 && (
              <p className="px-4 py-6 text-sm text-gray-500 text-center">
                No options available
              </p>
            )}
          </div>
          <div className="tpl-fmenu-foot">
            <button type="button" onClick={handleApply} className="ping-btn ping-btn-primary ping-btn-sm">
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
